import { useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";

const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");
export const fmtDMY = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || ""); return m ? `${m[3]}/${m[2]}/${m[1]}` : ""; };

// Same flow as the app's DatePickerField: always year → month → day; value stored as YYYY-MM-DD.
export function DatePickerField({ value, onChange, placeholder, testid = "date", yearRange = [-10, 10] }) {
  const { lang, dir } = useLang();
  const L = (ar, en) => (lang === "en" ? en : ar);
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const now = new Date();
  const cur = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  const base = Array.from({ length: yearRange[1] - yearRange[0] + 1 }, (_, i) => now.getFullYear() + yearRange[0] + i);
  const years = cur && !base.includes(Number(cur[1])) ? [...base, Number(cur[1])].sort((a, b) => a - b) : base;
  const invalid = !!value && !cur;
  const step = year == null ? "year" : month == null ? "month" : "day";
  const months = lang === "en" ? MONTHS_EN : MONTHS_AR;
  const days = year != null && month != null ? new Date(year, month, 0).getDate() : 0;
  const start = () => { setYear(null); setMonth(null); setOpen(true); };
  const pick = (y, m, d) => { onChange(`${y}-${pad(m)}-${pad(d)}`); setOpen(false); };
  const Back = dir === "rtl" ? ChevronRight : ChevronLeft;
  const Opt = ({ label, on, onClick, k }) => (
    <button type="button" onClick={onClick} data-testid={`${testid}-opt-${k}`}
      className={`h-12 rounded-xl border text-sm font-semibold transition-colors ${on ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E5E5E0] hover:border-[#064E3B]"}`}>{label}</button>
  );
  const title = step === "year" ? L("اختر السنة", "Choose year") : step === "month" ? `${L("اختر الشهر", "Choose month")} · ${year}` : `${L("اختر اليوم", "Choose day")} · ${months[month - 1]} ${year}`;
  return (
    <>
      <button type="button" onClick={start} data-testid={testid}
        className="mt-1 w-full h-12 rounded-xl border border-[#E5E5E0] bg-white px-4 flex items-center justify-between gap-2 text-sm font-normal hover:border-[#064E3B] transition-colors">
        <span className={cur ? "text-slate-900" : "text-slate-400"} dir="ltr" data-testid={`${testid}-value`}>{fmtDMY(value) || placeholder || "DD/MM/YYYY"}</span>
        <CalendarDays className="w-4 h-4 text-[#064E3B]" />
      </button>
      {invalid && <p className="text-[11px] text-red-600 mt-1" data-testid={`${testid}-invalid`}>{L("التاريخ المحفوظ غير صالح، اختر التاريخ من جديد", "Saved date is invalid — please pick it again")}</p>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid={`${testid}-sheet`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2 pr-8">
            {step !== "year" && <button type="button" onClick={() => (step === "day" ? setMonth(null) : setYear(null))} data-testid={`${testid}-back`}><Back className="w-5 h-5" /></button>}
            {title}</DialogTitle></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            {step === "year" && <div className="grid grid-cols-3 gap-2">{[...years].reverse().map((y) => <Opt key={y} k={y} label={y} on={cur && Number(cur[1]) === y} onClick={() => setYear(y)} />)}</div>}
            {step === "month" && <div className="grid grid-cols-3 gap-2">{months.map((m, i) => <Opt key={m} k={i + 1} label={m} on={cur && Number(cur[1]) === year && Number(cur[2]) === i + 1} onClick={() => setMonth(i + 1)} />)}</div>}
            {step === "day" && <div className="grid grid-cols-7 gap-1.5" dir="ltr">{Array.from({ length: days }, (_, i) => i + 1).map((d) => <Opt key={d} k={d} label={d} on={cur && fmtDMY(value) === `${pad(d)}/${pad(month)}/${year}`} onClick={() => pick(year, month, d)} />)}</div>}
          </div>
          {years.includes(now.getFullYear()) && <button type="button" onClick={() => pick(now.getFullYear(), now.getMonth() + 1, now.getDate())} className="h-10 rounded-xl border border-[#064E3B] text-[#064E3B] text-sm font-bold" data-testid={`${testid}-today`}>{L("اليوم", "Today")}</button>}
        </DialogContent>
      </Dialog>
    </>
  );
}
